'use client';

import { c as _c } from "react/compiler-runtime";
import { SignIn, useAuth as useClerkAuth } from '@clerk/nextjs';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PRODUCT_NAME } from '@/const';
import { useAuth as useFirebaseAuth } from '@/context/AuthContext';
const redirectPath = '/dashboard';
export default function FirebaseAuth() {
  const $ = _c(35);
  const router = useRouter();
  const pathname = usePathname();
  const {
    userId: clerkUserId,
    isLoaded: clerkIsLoaded
  } = useClerkAuth();
  const {
    firebaseUser,
    login,
    isLoggingIn,
    isLoggingOut,
    tokenVerified,
    isAuthReady
  } = useFirebaseAuth();
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const hasStartedRedirectRef = useRef(false);
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname === "/auth/signin" || pathname?.startsWith("/auth/signin/") || pathname === "/sso-callback";
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isAuthRoute = t0;
  const readyToRedirect = isAuthReady && !!firebaseUser && !!clerkUserId && !isLoggingIn && !isLoggingOut && tokenVerified;
  let t1;
  let t2;
  if ($[2] !== isAuthRoute || $[3] !== readyToRedirect) {
    t1 = () => {
      if (!readyToRedirect || !isAuthRoute || hasStartedRedirectRef.current) {
        return;
      }
      hasStartedRedirectRef.current = true;
      console.log("[auth] User fully authenticated, redirecting to dashboard");
      const redirectButtonTimer = setTimeout(() => setShowRedirectMessage(true), 3000);
      window.location.replace(redirectPath);
      return () => clearTimeout(redirectButtonTimer);
    };
    t2 = [readyToRedirect, isAuthRoute];
    $[2] = isAuthRoute;
    $[3] = readyToRedirect;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== isAuthRoute || $[7] !== readyToRedirect || $[8] !== router) {
    t3 = () => {
      if (!readyToRedirect || isAuthRoute || hasStartedRedirectRef.current) {
        return;
      }
      hasStartedRedirectRef.current = true;
      router.replace(redirectPath);
    };
    t4 = [readyToRedirect, isAuthRoute, router];
    $[6] = isAuthRoute;
    $[7] = readyToRedirect;
    $[8] = router;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const authLoadingBackdropStyle = t5 as const;
  if (clerkIsLoaded && !clerkUserId) {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <DefaultClerkSignIn />;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (!isAuthReady) {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="absolute inset-0 bg-black/30" />;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="fixed inset-0 flex items-center justify-center" style={authLoadingBackdropStyle}>{t6}<div className="relative z-10 flex flex-col items-center gap-2"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" /><p className="text-white text-base font-medium">Initializing authentication...</p></div></div>;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  if (isLoggingIn || isLoggingOut) {
    console.log("[auth] Loading:", {
      isLoggingIn,
      isLoggingOut
    });
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="absolute inset-0 bg-black/30" />;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" />;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const t8 = isLoggingIn ? "Logging in..." : "Logging out...";
    let t9;
    if ($[17] !== t8) {
      t9 = <div className="fixed inset-0 flex items-center justify-center" style={authLoadingBackdropStyle}>{t6}<div className="relative z-10 flex flex-col items-center gap-2">{t7}<p className="text-white text-base font-medium">{t8}</p></div></div>;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  if (!firebaseUser) {
    let t6;
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        backgroundImage: "url(/genesys-bg.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed"
      };
      t7 = <div className="absolute inset-0 bg-black/30" />;
      $[19] = t6;
      $[20] = t7;
    } else {
      t6 = $[19];
      t7 = $[20];
    }
    let t8;
    let t9;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" />;
      t9 = <p className="text-white text-base font-medium mb-4">Restoring session...</p>;
      $[21] = t8;
      $[22] = t9;
    } else {
      t8 = $[21];
      t9 = $[22];
    }
    let t10;
    if ($[23] !== login) {
      t10 = <div className="fixed inset-0 flex items-center justify-center" style={t6}>{t7}<div className="relative z-10 flex flex-col items-center justify-center p-4">{t8}{t9}<button onClick={() => login()} className="rounded-full border border-solid border-white/20 transition-colors flex items-center justify-center bg-white/10 text-white gap-2 hover:bg-white/20 font-medium text-sm sm:text-base h-10 sm:h-12 px-4 sm:px-5 sm:w-auto">Continue</button></div></div>;
      $[23] = login;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    return t10;
  }
  let t6;
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    t7 = <div className="absolute inset-0 bg-black/30" />;
    $[25] = t6;
    $[26] = t7;
  } else {
    t6 = $[25];
    t7 = $[26];
  }
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2" />;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = readyToRedirect ? "Redirecting to dashboard" : "Loading";
  let t10;
  if ($[28] !== t9) {
    t10 = <p className="text-white text-lg font-medium">{t9}</p>;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== showRedirectMessage) {
    t11 = showRedirectMessage && <Button onClick={_temp} variant="clear">If you are not redirected after 5 seconds, click here to go to dashboard</Button>;
    $[30] = showRedirectMessage;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11) {
    t12 = <div className="fixed inset-0 flex items-center justify-center" style={t6}>{t7}<div className="relative z-10 flex flex-col items-center gap-4">{t8}{t10}{t11}</div></div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp() {
  window.location.replace(redirectPath);
}
export const DefaultClerkSignIn = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="fixed inset-0 flex items-center justify-center" style={t0}><div className="relative z-10 flex flex-col items-center gap-6 sm:gap-8 lg:gap-10 2xl:gap-[2.22vh] px-4 py-8 sm:px-0 sm:py-0 max-sm:landscape:gap-3 max-sm:landscape:py-4"><div className="flex justify-center items-center shrink-0 opacity-70 w-56 h-10 sm:w-64 sm:h-12 md:w-80 md:h-14 lg:w-96 lg:h-16 xl:w-[26rem] xl:h-20 2xl:w-[22vw] 2xl:h-[4vh] max-sm:landscape:w-40 max-sm:landscape:h-7"><Image src="/ProductLogo.svg" alt={PRODUCT_NAME} width={753} height={128} className="w-full h-full object-contain" /></div><SignIn forceRedirectUrl={redirectPath} /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};